import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Filter, Search, AlertCircle, Info, FileText, X } from 'lucide-react';
import { useSearch } from '../hooks/useSearch';
import { useToggle } from '../hooks/useToggle';
import { highlightText } from '../utils/highlightText';
import SearchForm from '../components/SearchForm';
import LoadingSpinner from '../components/LoadingSpinner';
import NoResults from '../components/NoResults';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { Pagination, TabPagination } from '../components/Pagination';
import DownloadButton from '../components/DownloadButton';
import HowToCite from '../components/HowToCite';

const PaperSearch = () => {
  const {
    searchResults,
    setSearchResults,
    autocompleteResults,
    isLoading,
    searchTerm,
    setSearchTerm,
    showAutocomplete,
    autocompleteRef,
    inputRef,
    handleInputChange,
    handleAutocompleteClick,
    handleSearchSubmit,
    clearSearch,
    setShowAutocomplete,
    handleFullSearch,
    // Pagination related
    pagination,
    handlePageChange,
    handlePageSizeChange,
    // Tab data management
    tabData,
    tabLoading,
    tabErrors,
    fetchTabData,
  } = useSearch('/api/paper/search', 'paper');

  const { expandedCards, toggleCard } = useToggle();
  const [geneFilters, setGeneFilters] = useState({});
  const [geneLocalizationFilters, setGeneLocalizationFilters] = useState({});
  const [highlightEnabled, setHighlightEnabled] = useState(false);
  const [geneHighlightEnabled, setGeneHighlightEnabled] = useState(false);
  const [geneDisplayMode, setGeneDisplayMode] = useState({});
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [activeTabs, setActiveTabs] = useState({});
  const [paperLoadingState, setPaperLoadingState] = useState({});

  useEffect(() => {
    document.title = 'Synapse Proteomic Publication Search';
    const handleClickOutside = (event) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [autocompleteRef, inputRef, setShowAutocomplete]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        setShowDebugPanel((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (q) {
      setSearchTerm(q);
      if (inputRef.current) {
        inputRef.current.value = q;
      }
      handleFullSearch(q);
    }
  }, [setSearchTerm, handleFullSearch, inputRef]);

  // Check if a specific paper is loaded
  const isPaperLoaded = (pmid) => {
    return Object.keys(tabData).some(key => key.startsWith(`${pmid}-`));
  };

  // Check if a paper is currently loading
  const isPaperLoading = (pmid) => {
    return paperLoadingState[pmid] || false;
  };

  // Load paper details including genes and mutations
  const loadPaperDetails = async (pmid) => {
    if (isPaperLoaded(pmid) || isPaperLoading(pmid)) {
      return;
    }

    try {
      setPaperLoadingState(prev => ({ ...prev, [pmid]: true }));

      // Load genes data first
      await fetchPaperGenesWithFilters(pmid, {
        page: 1,
        pageSize: searchResults.find(p => p.PMID === pmid)?.genes_pagination?.pageSize || 20,
        geneFilter: geneFilters[pmid]?.term || '',
        geneLocalization: geneLocalizationFilters[pmid] || 'All',
        geneDisplayMode: geneDisplayMode[pmid] || 'consensus',
        preserveTotalCount: true
      });

      // If the paper has mutations, load them too
      const paper = searchResults.find(p => p.PMID === pmid);
      if (paper && (paper.mutations_pagination?.total > 0 || (paper.associated_mutations && paper.associated_mutations.length > 0))) {
        await fetchPaperMutations(pmid, 1, 20);
      }

      return true;
    } catch (error) {
      console.error(`Error loading paper details for PMID ${pmid}:`, error);
      return false;
    } finally {
      setPaperLoadingState(prev => ({ ...prev, [pmid]: false }));
    }
  };

  const fetchPaperGenesWithFilters = async (pmid, filters = {}) => {
    if (!searchTerm && !pmid) return;

    try {
      const apiUrl = '/api/paper/genes/filter';
      const params = {
        pmid,
        page: filters.page || 1,
        pageSize: filters.pageSize || 20,
      };

      if (filters.geneFilter) params.term = filters.geneFilter;
      if (filters.geneLocalization !== 'All') params.localization = filters.geneLocalization;
      if (filters.geneDisplayMode !== 'all') params.mode = filters.geneDisplayMode;

      const result = await fetchTabData(pmid, 'genes', apiUrl, params);

      if (result && result.success) {
        const updatedResults = searchResults.map(paper => {
          if (paper.PMID === pmid) {
            const updatedPaper = {
              ...paper,
              associated_genes: result.data,
              consensus_stats: result.consensus_stats || paper.consensus_stats,
              available_localizations: result.available_localizations || paper.available_localizations,
              genes_pagination: result.pagination || paper.genes_pagination,
              // Preserve total_genes_count, only set if not already present
              total_genes_count: paper.total_genes_count || result.count?.genes || result.pagination?.total || 0
            };

            // Update consensus count when available
            if (result.count && result.count.consensus_genes) {
              if (!updatedPaper.consensus_stats) updatedPaper.consensus_stats = {};
              updatedPaper.consensus_stats.total_consensus_genes = result.count.consensus_genes;
            }

            return updatedPaper;
          }
          return paper;
        });

        setSearchResults(updatedResults);
      }

      return result;
    } catch (error) {
      console.error('Error fetching publication genes with filters:', error);
      throw error;
    }
  };

  // Fetch paper mutations with pagination
  const fetchPaperMutations = async (pmid, page = 1, pageSize = 20) => {
    if (!searchTerm && !pmid) return;

    try {
      // Build API endpoint URL with parameters
      const apiUrl = '/api/paper/mutations/filter';
      const params = {
        pmid,
        page,
        pageSize,
      };

      // Fetch tab data
      const result = await fetchTabData(pmid, 'mutations', apiUrl, params);

      if (result && result.success) {
        // Update the searchResults with the new mutation data
        const updatedResults = searchResults.map(paper => {
          if (paper.PMID === pmid) {
            return {
              ...paper,
              associated_mutations: result.data,
              mutations_pagination: result.pagination || paper.mutations_pagination
            };
          }
          return paper;
        });

        setSearchResults(updatedResults);
      }

      return result;
    } catch (error) {
      console.error('Error fetching publication mutations:', error);
      throw error;
    }
  };

  const getConsensusGeneCount = (paper) => {
    return paper?.consensus_stats?.total_consensus_genes || 0;
  };

  const getAvailableLocalizations = (paper) => {
    return paper?.available_localizations || ['All'];
  };

  const handleGeneFilter = async (pmid, filterTerm) => {
    setActiveTabs(prev => ({ ...prev, [pmid]: 'genes' }));
    setGeneFilters(prev => ({
      ...prev,
      [pmid]: filterTerm ? { term: filterTerm.toLowerCase() } : null
    }));

    if (!isPaperLoaded(pmid)) {
      await loadPaperDetails(pmid);
    }

    fetchPaperGenesWithFilters(pmid, {
      page: 1,
      pageSize: searchResults.find(p => p.PMID === pmid)?.genes_pagination?.pageSize || 20,
      geneFilter: filterTerm,
      geneLocalization: geneLocalizationFilters[pmid] || 'All',
      geneDisplayMode: geneDisplayMode[pmid] || 'consensus'
    });
  };

  const handleGeneLocalizationFilter = async (pmid, localization) => {
    setActiveTabs(prev => ({ ...prev, [pmid]: 'genes' }));
    setGeneLocalizationFilters(prev => ({ ...prev, [pmid]: localization }));

    if (!isPaperLoaded(pmid)) {
      await loadPaperDetails(pmid);
    }

    fetchPaperGenesWithFilters(pmid, {
      page: 1,
      pageSize: searchResults.find(p => p.PMID === pmid)?.genes_pagination?.pageSize || 20,
      geneFilter: geneFilters[pmid]?.term || '',
      geneLocalization: localization,
      geneDisplayMode: geneDisplayMode[pmid] || 'consensus'
    });
  };

  const handleGeneDisplayMode = async (pmid, mode) => {
    setActiveTabs(prev => ({ ...prev, [pmid]: 'genes' }));
    setGeneDisplayMode(prev => ({ ...prev, [pmid]: mode }));

    if (!isPaperLoaded(pmid)) {
      await loadPaperDetails(pmid);
    }

    fetchPaperGenesWithFilters(pmid, {
      page: 1,
      pageSize: searchResults.find(p => p.PMID === pmid)?.genes_pagination?.pageSize || 20,
      geneFilter: geneFilters[pmid]?.term || '',
      geneLocalization: geneLocalizationFilters[pmid] || 'All',
      geneDisplayMode: mode
    });
  };

  const isConsensusGene = (gene) => {
    return gene.is_consensus === true;
  };

  const setActiveTab = async (pmid, tabName) => {
    setActiveTabs(prev => ({ ...prev, [pmid]: tabName }));

    if (!expandedCards[pmid]) {
      toggleCard(pmid);
    }

    if (!isPaperLoaded(pmid)) {
      await loadPaperDetails(pmid);
    }
  };

  const handleGenePageChange = async (pmid, newPage) => {
    if (!isPaperLoaded(pmid)) {
      await loadPaperDetails(pmid);
    }

    fetchPaperGenesWithFilters(pmid, {
      page: newPage,
      pageSize: searchResults.find(p => p.PMID === pmid)?.genes_pagination?.pageSize || 20,
      geneFilter: geneFilters[pmid]?.term || '',
      geneLocalization: geneLocalizationFilters[pmid] || 'All',
      geneDisplayMode: geneDisplayMode[pmid] || 'consensus'
    });
  };

  const handleGenePageSizeChange = async (pmid, newPageSize) => {
    if (!isPaperLoaded(pmid)) {
      await loadPaperDetails(pmid);
    }

    fetchPaperGenesWithFilters(pmid, {
      page: 1,
      pageSize: newPageSize,
      geneFilter: geneFilters[pmid]?.term || '',
      geneLocalization: geneLocalizationFilters[pmid] || 'All',
      geneDisplayMode: geneDisplayMode[pmid] || 'consensus'
    });
  };

  const handleMutationPageChange = async (pmid, newPage) => {
    if (!isPaperLoaded(pmid)) {
      await loadPaperDetails(pmid);
    }

    fetchPaperMutations(pmid, newPage, searchResults.find(p => p.PMID === pmid)?.mutations_pagination?.pageSize || 20);
  };

  const handleMutationPageSizeChange = async (pmid, newPageSize) => {
    if (!isPaperLoaded(pmid)) {
      await loadPaperDetails(pmid);
    }

    fetchPaperMutations(pmid, 1, newPageSize);
  };

  const handleCardClick = async (pmid) => {
    toggleCard(pmid);

    // If the card is being expanded and no tab is active, set genes as the default tab
    if (!expandedCards[pmid]) {
      setActiveTabs(prev => ({ ...prev, [pmid]: 'genes' }));

      // Load paper details if not loaded yet
      if (!isPaperLoaded(pmid) && !isPaperLoading(pmid)) {
        loadPaperDetails(pmid);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FileText size={24} className="mr-2 text-blue-600" />Synapse Proteomic Publication Search
        </h1>
        <p className='mb-8 mt-2 text-gray-600'>Find Synapse proteomics studies exploring gene-mutation interactions.</p>

        <SearchForm
          searchTerm={searchTerm}
          handleInputChange={handleInputChange}
          handleSearchSubmit={handleSearchSubmit}
          clearSearch={clearSearch}
          autocompleteResults={autocompleteResults}
          showAutocomplete={showAutocomplete}
          handleAutocompleteClick={handleAutocompleteClick}
          inputRef={inputRef}
          autocompleteRef={autocompleteRef}
          setShowAutocomplete={setShowAutocomplete}
          highlightEnabled={highlightEnabled}
          setHighlightEnabled={setHighlightEnabled}
          placeholder="Search by Publication title, PMID or Year..."
          highlightText={highlightText}
        />

        <p className='mt-1 text-xs text-gray-400 mb-8'> Accepted keywords [PubMed ID, Publication title, Year, Description]</p>

        {showDebugPanel && (
          <div className="mb-4 p-4 bg-gray-100 border border-gray-300 rounded">
            <h3 className="font-medium mb-2">Debug Information</h3>
            <div className="text-sm space-y-1">
              <p>Pagination: Page {pagination.page} of {pagination.totalPages} (Total: {pagination.total})</p>
              <p>Total Consensus Genes: {searchResults.reduce((sum, paper) => sum + (paper.consensus_stats?.total_consensus_genes || 0), 0)}</p>
              <div className="mt-2 border-t border-gray-300 pt-2">
                <p className="font-medium">Sample Publication Matches:</p>
                <ul className="pl-5 list-disc">
                  {searchResults.slice(0, 3).map((paper, idx) => {
                    const consensusCount = paper.consensus_stats?.total_consensus_genes || 0;
                    const totalGenes = paper.genes_pagination?.total || 0;
                    return (
                      <li key={idx} className={consensusCount > 0 ? 'text-green-600' : 'text-red-600'}>
                        {paper.name?.substring(0, 30)}...
                        ({consensusCount > 0 ? `${consensusCount} of ${totalGenes} consensus genes (${paper.consensus_stats?.consensus_percentage || 0}%)` : 'No consensus genes'})
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        )}

        {isLoading && <LoadingSpinner />}

        {!isLoading && pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            className="mb-6"
          />
        )}

        <div className="space-y-6">
          {searchResults.map((paper) => (
            <div key={paper.PMID} className="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200">
              <div
                className="px-6 py-5 cursor-pointer hover:bg-gray-50 transition-colors duration-150"
                onClick={() => handleCardClick(paper.PMID)}
              >
                <div className="flex flex-col">
                  {/* Top row with title and action buttons */}
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-medium text-gray-900">
                      {highlightText(paper.paperTitle, searchTerm, highlightEnabled)}
                    </h3>

                    {/* Action buttons */}
                    <div className="flex items-center shrink-0">
                      <div>
                        <DownloadButton
                          url={`/api/paper/${paper.PMID}/csv`}
                          label="Download Paper Data"
                        />
                      </div>
                      <div className="ml-4 p-1 rounded-full hover:bg-gray-200 transition-colors duration-150">
                        {expandedCards[paper.PMID] ? (
                          <ChevronUp className="h-6 w-6 text-gray-600" />
                        ) : (
                          <ChevronDown className="h-6 w-6 text-gray-600" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Second row with metadata */}
                  <div className="mt-2 flex justify-between items-center">
                    {/* Metadata on the left */}
                    <div className="flex space-x-12">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">PubMed ID</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          <a
                            href={`https://pubmed.ncbi.nlm.nih.gov/${paper.PMID}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-blue-600 hover:text-blue-800 no-underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {highlightText(paper.PMID, searchTerm, highlightEnabled)}
                            <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                          </a>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Year</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {highlightText(paper.year, searchTerm, highlightEnabled)}
                        </dd>
                      </div>
                    </div>

                    {/* Empty div to maintain layout */}
                    <div></div>
                  </div>
                </div>
              </div>

              <div className="px-6">
                <div className="bg-white">
                  <nav className="flex -mb-px">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveTab(paper.PMID, 'genes');
                      }}
                      className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${expandedCards[paper.PMID] && (!activeTabs[paper.PMID] || activeTabs[paper.PMID] === 'genes')
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                      <div className="flex items-center">
                        <span>Genes</span>
                        <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                          Total: {paper.total_genes_count || 0} {/* Use total_genes_count */}
                        </span>
                        {paper.consensus_stats?.total_consensus_genes > 0 && (
                          <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                            Consensus: {paper.consensus_stats.total_consensus_genes}
                          </span>
                        )}
                      </div>
                    </button>
                    {(paper.mutations_pagination?.total > 0 || (paper.associated_mutations && paper.associated_mutations.length > 0)) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab(paper.PMID, 'mutations');
                        }}
                        className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${expandedCards[paper.PMID] && activeTabs[paper.PMID] === 'mutations'
                          ? 'border-amber-500 text-amber-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                      >
                        <div className="flex items-center">
                          <span>Mutations</span>
                          <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">
                            {paper.mutations_pagination?.total || paper.associated_mutations?.length || 0}
                          </span>
                        </div>
                      </button>
                    )}
                  </nav>
                </div>
              </div>

              {expandedCards[paper.PMID] && (
                <div className="px-6">
                  <div className="p-6">
                    {(!activeTabs[paper.PMID] || activeTabs[paper.PMID] === 'genes') && (
                      <div>
                        <div className="flex flex-wrap items-center gap-3 mb-4 justify-end">
                          <div className="flex items-center">
                            <Filter className="h-4 w-4 text-gray-400 mr-2" />
                            <select
                              className="pl-2 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                              onChange={(e) => handleGeneLocalizationFilter(paper.PMID, e.target.value)}
                              value={geneLocalizationFilters[paper.PMID] || 'All'}
                            >
                              {getAvailableLocalizations(paper).map((loc, index) => (
                                <option key={index} value={loc}>
                                  {loc === 'All' ? 'All Localizations' : loc}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex items-center">
                            <Filter className="h-4 w-4 text-gray-400 mr-2" />
                            <select
                              className="pl-2 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                              onChange={(e) => handleGeneDisplayMode(paper.PMID, e.target.value)}
                              value={geneDisplayMode[paper.PMID] || 'consensus'}
                            >
                              <option value="all">All Genes</option>
                              <option value="consensus">Consensus Genes</option>
                            </select>
                          </div>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Filter genes or proteins..."
                              className="pl-9 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              onChange={(e) => handleGeneFilter(paper.PMID, e.target.value)}
                            />
                            {geneFilters[paper.PMID] && (
                              <button
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const input = e.currentTarget.parentNode.querySelector('input');
                                  if (input) input.value = '';
                                  handleGeneFilter(paper.PMID, '');
                                }}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <button
                            onClick={() => setGeneHighlightEnabled(!geneHighlightEnabled)}
                            className={`px-2 py-1 rounded-md text-xs font-medium ${geneHighlightEnabled
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                          >
                            Highlight {geneHighlightEnabled ? 'On' : 'Off'}
                          </button>
                          <div>
                            <DownloadButton
                              url={`/api/paper/${paper.PMID}/allgenes/csv`}
                              label="Download Gene Data"
                            />
                          </div>
                        </div>

                        {isPaperLoading(paper.PMID) && <LoadingSpinner />}

                        {!isPaperLoading(paper.PMID) && (
                          <>
                            {paper.genes_pagination && paper.genes_pagination.totalPages > 1 && (
                              <TabPagination
                                pagination={paper.genes_pagination}
                                onPageChange={(newPage) => handleGenePageChange(paper.PMID, newPage)}
                                onPageSizeChange={(newPageSize) => handleGenePageSizeChange(paper.PMID, newPageSize)}
                                className="mb-4"
                              />
                            )}

                            {paper.associated_genes && paper.associated_genes.length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                {paper.associated_genes.map((gene, index) => (
                                  <div key={index} className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
                                    <h5 className="font-medium text-gray-900 truncate flex items-center">
                                      <a
                                        href={`/gene-search?q=${encodeURIComponent(gene.human_name)}`}
                                        className="hover:text-blue-600 mr-2"
                                      >
                                        <span className="italic">
                                          {highlightText(
                                            gene.human_name,
                                            geneFilters[paper.PMID]?.term || '',
                                            geneHighlightEnabled
                                          )}
                                        </span>
                                      </a>
                                      {isConsensusGene(gene) && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          Consensus
                                        </span>
                                      )}
                                    </h5>
                                    <div className="mt-3 text-sm">
                                      <table className="min-w-full">
                                        <tbody className="divide-y divide-gray-200">
                                          <tr>
                                            <td className="py-1 pr-2 text-gray-500 font-medium w-1/3">Human Entrez:</td>
                                            <td className="py-1">
                                              <a
                                                href={`https://www.ncbi.nlm.nih.gov/gene/${gene.human_entrez}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center text-blue-600 hover:text-blue-800 no-underline"
                                              >
                                                {highlightText(
                                                  gene.human_entrez,
                                                  geneFilters[paper.PMID]?.term || '',
                                                  geneHighlightEnabled
                                                )}
                                                <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                              </a>
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="py-1 pr-2 text-gray-500 font-medium">Mouse Entrez:</td>
                                            <td className="py-1">
                                              <a
                                                href={`https://www.ncbi.nlm.nih.gov/gene/${gene.mouse_entrez}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center text-blue-600 hover:text-blue-800 no-underline"
                                              >
                                                {highlightText(
                                                  gene.mouse_entrez,
                                                  geneFilters[paper.PMID]?.term || '',
                                                  geneHighlightEnabled
                                                )}
                                                <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                              </a>
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="py-1 pr-2 text-gray-500 font-medium">Rat Entrez:</td>
                                            <td className="py-1">
                                              <a
                                                href={`https://www.ncbi.nlm.nih.gov/gene/${gene.rat_entrez}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center text-blue-600 hover:text-blue-800 no-underline"
                                              >
                                                {highlightText(
                                                  gene.rat_entrez,
                                                  geneFilters[paper.PMID]?.term || '',
                                                  geneHighlightEnabled
                                                )}
                                                <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                              </a>
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="py-1 pr-2 text-gray-500 font-medium">MGI:</td>
                                            <td className="py-1">
                                              <a
                                                href={`https://www.informatics.jax.org/marker/${gene.mgi}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center text-blue-600 hover:text-blue-800 no-underline"
                                              >
                                                {highlightText(
                                                  gene.mgi,
                                                  geneFilters[paper.PMID]?.term || '',
                                                  geneHighlightEnabled
                                                )}
                                                <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                              </a>
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="py-1 pr-2 text-gray-500 font-medium">Dataset:</td>
                                            <td className="py-1">
                                              {highlightText(
                                                gene.dataset,
                                                geneFilters[paper.PMID]?.term || '',
                                                geneHighlightEnabled
                                              )}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="py-1 pr-2 text-gray-500 font-medium">Species:</td>
                                            <td className="py-1">
                                              {highlightText(
                                                `${gene.species?.sci_name || ''} ${gene.species?.name ? `(${gene.species.name})` : ''}`,
                                                geneFilters[paper.PMID]?.term || '',
                                                geneHighlightEnabled
                                              )}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="py-1 pr-2 text-gray-500 font-medium">Brain Region:</td>
                                            <td className="py-1">
                                              {highlightText(
                                                `${gene.brain_region?.name || ''} ${gene.brain_region?.description ? `(${gene.brain_region.description})` : ''}`,
                                                geneFilters[paper.PMID]?.term || '',
                                                geneHighlightEnabled
                                              )}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="py-1 pr-2 text-gray-500 font-medium">Localisation:</td>
                                            <td className="py-1">
                                              {highlightText(
                                                gene.localisation?.name,
                                                geneFilters[paper.PMID]?.term || '',
                                                geneHighlightEnabled
                                              )}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="py-1 pr-2 text-gray-500 font-medium">Method:</td>
                                            <td className="py-1">
                                              {highlightText(
                                                gene.method?.name,
                                                geneFilters[paper.PMID]?.term || '',
                                                geneHighlightEnabled
                                              )}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-8">
                                <p className="text-gray-500">No genes associated with this publication.</p>
                              </div>
                            )}

                            {paper.genes_pagination && paper.genes_pagination.totalPages > 1 && (
                              <TabPagination
                                pagination={paper.genes_pagination}
                                onPageChange={(newPage) => handleGenePageChange(paper.PMID, newPage)}
                                onPageSizeChange={(newPageSize) => handleGenePageSizeChange(paper.PMID, newPageSize)}
                                className="mt-4"
                                showPageSize={false}
                              />
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {activeTabs[paper.PMID] === 'mutations' && (
                      <div>
                        <div className="flex items-center justify-end mb-4 space-x-4">
                          <DownloadButton
                            url={`/api/paper/${paper.PMID}/allmutations/csv`}
                            label="Download Mutations"
                          />
                        </div>

                        {isPaperLoading(paper.PMID) && <LoadingSpinner />}

                        {!isPaperLoading(paper.PMID) && (
                          <>
                            {paper.mutations_pagination && paper.mutations_pagination.totalPages > 1 && (
                              <TabPagination
                                pagination={paper.mutations_pagination}
                                onPageChange={(newPage) => handleMutationPageChange(paper.PMID, newPage)}
                                onPageSizeChange={(newPageSize) => handleMutationPageSizeChange(paper.PMID, newPageSize)}
                                className="mb-4"
                              />
                            )}

                            {paper.associated_mutations && paper.associated_mutations.length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {paper.associated_mutations.map((mutation, index) => (
                                  <div key={index} className="bg-gray-50 rounded-md p-4 shadow-sm">
                                    <div className="mb-2">
                                      <h5 className="text-sm font-semibold text-gray-900 mb-1">
                                        {mutation.variant || mutation.Variant || 'Unknown Variant'}
                                      </h5>
                                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
                                        <div>
                                          <span className="font-medium">Chromosome:</span>
                                          <span className="ml-1">{mutation.chromosome || mutation.Chromosome || 'N/A'}</span>
                                        </div>
                                        <div>
                                          <span className="font-medium">Position:</span>
                                          <span className="ml-1">{mutation.position || mutation.Position || 'N/A'}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="mt-2 space-y-1 text-xs text-gray-600">
                                      <div className="mb-1">
                                        <span className="font-medium">Gene:</span>
                                        <span className="ml-1">
                                          <a
                                            href={`/gene-search?q=${encodeURIComponent(mutation.geneName || mutation.GeneName || '')}`}
                                            className="text-blue-600 hover:underline"
                                          >
                                            {mutation.geneName || mutation.GeneName || 'N/A'}
                                          </a>
                                        </span>
                                      </div>
                                      {(mutation.humanEntrez || mutation.HumanEntrez) && (
                                        <div className="mb-1">
                                          <span className="font-medium">Human Entrez:</span>
                                          <span className="ml-1">
                                            <a
                                              href={`https://www.ncbi.nlm.nih.gov/gene/${mutation.humanEntrez || mutation.HumanEntrez}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:underline"
                                            >
                                              {mutation.humanEntrez || mutation.HumanEntrez}
                                              <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                            </a>
                                          </span>
                                        </div>
                                      )}
                                      <div>
                                        <span className="font-medium">Function Class:</span>
                                        <span className="ml-1">{mutation.functionClass || mutation.FunctionClass || 'N/A'}</span>
                                      </div>
                                      <div>
                                        <span className="font-medium">cDNA Variant:</span>
                                        <span className="ml-1">{mutation.cdnaVariant || mutation.cDNAvariant || 'N/A'}</span>
                                      </div>
                                      <div>
                                        <span className="font-medium">Protein Variant:</span>
                                        <span className="ml-1">{mutation.proteinVariant || mutation.ProteinVariant || 'N/A'}</span>
                                      </div>
                                      {(mutation.exonIntron || mutation.ExonIntron) && (
                                        <div>
                                          <span className="font-medium">Exon/Intron:</span>
                                          <span className="ml-1">{mutation.exonIntron || mutation.ExonIntron}</span>
                                        </div>
                                      )}
                                    </div>
                                    {((mutation.denovo !== undefined || mutation.DENOVO !== undefined) ||
                                      (mutation.sfari !== undefined || mutation.SFARI !== undefined) ||
                                      (mutation.epilepsyGene !== undefined || mutation.EpilepsyGene !== undefined) ||
                                      (mutation.clinVar || mutation.ClinVar)) && (
                                        <div className="mt-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
                                          <div className="grid grid-cols-2 gap-2">
                                            {(mutation.denovo !== undefined || mutation.DENOVO !== undefined) && (
                                              <div>
                                                <span className="font-medium">DENOVO:</span>
                                                <span className="ml-1">
                                                  {mutation.denovo || mutation.DENOVO ? 'Yes' : 'No'}
                                                </span>
                                              </div>
                                            )}
                                            {(mutation.sfari !== undefined || mutation.SFARI !== undefined) && (
                                              <div>
                                                <span className="font-medium">SFARI:</span>
                                                <span className="ml-1">
                                                  {mutation.sfari || mutation.SFARI ? 'Yes' : 'No'}
                                                </span>
                                              </div>
                                            )}
                                            {(mutation.epilepsyGene !== undefined || mutation.EpilepsyGene !== undefined) && (
                                              <div>
                                                <span className="font-medium">Epilepsy Gene:</span>
                                                <span className="ml-1">
                                                  {mutation.epilepsyGene || mutation.EpilepsyGene ? 'Yes' : 'No'}
                                                </span>
                                              </div>
                                            )}
                                            {(mutation.clinVar || mutation.ClinVar) && (
                                              <div>
                                                <span className="font-medium">ClinVar:</span>
                                                <span className="ml-1">{mutation.clinVar || mutation.ClinVar}</span>
                                              </div>
                                            )}
                                          </div>
                                          {(mutation.papers || mutation.Papers || mutation.associatedPapers) &&
                                            (mutation.papers || mutation.Papers || mutation.associatedPapers).length > 0 && (
                                              <div className="mt-2">
                                                <span className="font-medium">Associated Papers:</span>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                  {(mutation.papers || mutation.Papers || mutation.associatedPapers).map((pmid, idx) => (
                                                    <a
                                                      key={idx}
                                                      href={`https://pubmed.ncbi.nlm.nih.gov/${pmid}/`}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="text-blue-600 hover:underline"
                                                    >
                                                      {pmid}
                                                      <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                                    </a>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-8">
                                <p className="text-gray-500">No mutations associated with this publication.</p>
                              </div>
                            )}

                            {paper.mutations_pagination && paper.mutations_pagination.totalPages > 1 && (
                              <TabPagination
                                pagination={paper.mutations_pagination}
                                onPageChange={(newPage) => handleMutationPageChange(paper.PMID, newPage)}
                                onPageSizeChange={(newPageSize) => handleMutationPageSizeChange(paper.PMID, newPageSize)}
                                className="mt-4"
                                showPageSize={false}
                              />
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {!isLoading && searchResults.length > 0 && pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            className="mt-6"
          />
        )}

        {!isLoading && searchResults.length === 0 && <NoResults searchTerm={searchTerm} />}
        <section className="mt-24">
          <HowToCite />
        </section>
      </div>
    </div>
  );
};

export default PaperSearch;